/**
 * Per-tenant revenue table; expand a row for `byLineKind`. TMMPDM-0020: one totals row per
 * `totals[]` currency — never a cross-currency figure. TMMPDM-0037: Reseller margin and Reseller
 * net (after affiliates) replace Uplift, Tenant share and Platform share (tmm-api 0099).
 * TMMPDM-0035: sticky column headers on the populated table.
 */
import { Fragment, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Collapse,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { EmptyState } from '@/components/page';
import { Amount, stickyTableContainerSx } from '@/components/ui';
import type { TenantRevenue } from '@/api/tmm/types';
import type { RevenueData } from './useRevenue';
import { LineKindBreakdown } from './LineKindBreakdown';

interface RevenueByTenantTableProps {
  data: RevenueData | null;
  loading: boolean;
  locale: string;
}

export function RevenueByTenantTable({ data, loading, locale }: RevenueByTenantTableProps) {
  const { t } = useCopy();
  const [expanded, setExpanded] = useState<string | null>(null);

  const header = (
    <TableHead>
      <TableRow>
        <TableCell />
        <TableCell>{t('revenue.col.tenant', 'Tenant')}</TableCell>
        <TableCell align="right">{t('revenue.col.retail', 'Retail')}</TableCell>
        <TableCell align="right">{t('revenue.col.wholesale', 'Wholesale (our price)')}</TableCell>
        <TableCell align="right">{t('revenue.col.resellerMargin', 'Reseller margin')}</TableCell>
        <TableCell align="right">
          {t('revenue.col.resellerNet', 'Reseller net (after affiliates)')}
        </TableCell>
        <TableCell align="right">{t('revenue.col.tax', 'Tax')}</TableCell>
        <TableCell align="right">{t('revenue.col.refunds', 'Refunds')}</TableCell>
      </TableRow>
    </TableHead>
  );

  if (!data) {
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('revenue.tableAria', 'Revenue by tenant')}>
          {header}
          <TableBody>
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {loading ? t('common.loading', 'Loading') : null}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  const { report, tenantsBySlug } = data;
  const rows = report.tenants;

  if (rows.length === 0) {
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('revenue.tableAria', 'Revenue by tenant')}>
          {header}
          <TableBody>
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <EmptyState title={t('revenue.empty', 'No revenue in this period.')} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  const money = (row: TenantRevenue, minor: number) => {
    const currency = tenantsBySlug.get(row.slug)?.currency;
    return currency ? <Amount minor={minor} currency={currency} locale={locale} /> : '—';
  };

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined" sx={stickyTableContainerSx}>
        <Table size="small" stickyHeader aria-label={t('revenue.tableAria', 'Revenue by tenant')}>
          {header}
          <TableBody>
            {rows.map((row) => {
              const tenant = tenantsBySlug.get(row.slug);
              const currency = tenant?.currency ?? null;
              const open = expanded === row.slug;
              return (
                <Fragment key={row.slug}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        aria-label={
                          open
                            ? t('revenue.collapse', 'Collapse line kinds')
                            : t('revenue.expand', 'Expand line kinds')
                        }
                        onClick={() => setExpanded(open ? null : row.slug)}
                      >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/partners/by-tenant/${encodeURIComponent(row.slug)}`}
                        variant="body2"
                        underline="hover"
                      >
                        {tenant?.name ?? row.slug}
                      </Link>
                    </TableCell>
                    <TableCell align="right">{money(row, row.totals.retailMinor)}</TableCell>
                    <TableCell align="right">{money(row, row.totals.wholesaleMinor)}</TableCell>
                    <TableCell align="right">
                      {money(row, row.totals.resellerMarginMinor)}
                    </TableCell>
                    <TableCell align="right">{money(row, row.totals.tenantShareMinor)}</TableCell>
                    <TableCell align="right">{money(row, row.totals.taxMinor)}</TableCell>
                    <TableCell align="right">{money(row, row.totals.refundsMinor)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                      <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1 }}>
                          <LineKindBreakdown
                            rows={row.byLineKind}
                            currency={currency}
                            locale={locale}
                          />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
            {report.totals.map((entry) => (
              <TableRow key={`total-${entry.currency}`}>
                <TableCell />
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('dashboard.fleet.totalsInCurrency', 'Totals in {{currency}}', {
                      currency: entry.currency,
                    })}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Amount minor={entry.retailMinor} currency={entry.currency} locale={locale} />
                </TableCell>
                <TableCell align="right">
                  <Amount minor={entry.wholesaleMinor} currency={entry.currency} locale={locale} />
                </TableCell>
                <TableCell align="right">
                  <Amount
                    minor={entry.resellerMarginMinor}
                    currency={entry.currency}
                    locale={locale}
                  />
                </TableCell>
                <TableCell align="right">
                  <Amount
                    minor={entry.tenantShareMinor}
                    currency={entry.currency}
                    locale={locale}
                  />
                </TableCell>
                <TableCell align="right">
                  <Amount minor={entry.taxMinor} currency={entry.currency} locale={locale} />
                </TableCell>
                <TableCell align="right">
                  <Amount minor={entry.refundsMinor} currency={entry.currency} locale={locale} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
